<script>
    import { createEventDispatcher } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import { Icon } from '@oat-sa-private/ui-elements';

    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2025 (original work) Open Assessment Technologies SA ;

    /**
     * @property {boolean} removable - Defines whether chip is removable
     * @property {string} removeBtnLabel - Used to provide custom remove button label
     * @property {boolean} clickable - Defines whether chip itself is clickable
     */
    export let removable = true;
    export let removeBtnLabel = __('Remove');
    export let clickable = false;

    $: rootAttributes = clickable
        ? {
            role: 'button',
            tabindex: 0
        }
        : {};

    const dispatch = createEventDispatcher();

    function createEventForwarder(eventName) {
        return () => {
            dispatch(eventName);
        };
    }
</script>

<style>
    .chip {
        display: inline-flex;
        padding: 1rem;
        border: var(--border-thin) solid currentColor;
        border-radius: var(--radius-medium);
        align-items: center;
        background-color: var(--color-gs-light-alternative-bg);
        font-size: var(--fontsize-body-xs);

        &.clickable {
            &:hover,
            &:focus,
            &:focus-within {
                color: var(--color-brand);
                background-color: var(--color-brand-light);
            }

            &:focus {
                @add-mixin outline-focus 0;
            }
        }
    }

    .remove-btn {
        --size: 2rem;
        display: flex;
        width: var(--size);
        height: var(--size);
        align-items: center;
        justify-content: center;
        border: unset;
        padding: unset;
        margin: unset;
        cursor: pointer;
        color: unset;
        background: unset;
        &:focus {
            @add-mixin outline-focus 0;
        }
    }

    .chip :global(* + *) {
        margin-inline-start: 1rem;
    }
</style>

<span
    class="chip"
    class:clickable={clickable || removable}
    {...rootAttributes}
    on:click={clickable ? createEventForwarder('click') : () => {}}
>
    <span>
        <slot />
    </span>
    {#if removable}
        <button
            class="remove-btn"
            aria-label={removeBtnLabel}
            type="button"
            on:click|stopPropagation={createEventForwarder('remove')}
        >
            <Icon name="wrong-12" />
        </button>
    {/if}
</span>
